import React from 'react';
import { Heart, Wifi, Battery, Smartphone, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const DeviceCard = React.memo(({ device, serial, onCheckOnline, onToggleFavorite, onDelete }) => {
  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    onToggleFavorite();
  };

  const handleCheckClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    onCheckOnline();
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    if (window.confirm(`Are you sure you want to delete device ${device.id?.slice(-8)}? This action cannot be undone.`)) {
      onDelete();
    }
  };

  const getBatteryColorClass = (battery) => {
    if (battery > 50) return 'battery-high';
    if (battery > 20) return 'battery-medium';
    return 'battery-low';
  };

  const getProgressClass = (battery) => {
    if (battery > 50) return 'progress-high';
    if (battery > 20) return 'progress-medium';
    return 'progress-low';
  };

  const battery = Number(device.battery || 0);
  const batteryColorClass = getBatteryColorClass(battery);
  const progressClass = getProgressClass(battery);

  // Format install time
  const formatInstallTime = () => {
    if (device.installDate) {
      return device.installDate;
    }
    if (!device.installTime) return 'Recently';
    const date = new Date(device.installTime);
    if (isNaN(date.getTime())) return 'Recently';
    try {
      const month = date.toLocaleString('default', { month: 'short' });
      const day = date.getDate();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const period = hours >= 12 ? 'PM' : 'AM';
      if (hours > 12) hours -= 12;
      if (hours === 0) hours = 12;
      return `${month} ${day}, ${hours}:${minutes} ${period}`;
    } catch (error) {
      return 'Recently';
    }
  };

  return (
    <div className="device-card">
      <div className="card-header">
        <div className="serial-info">
          <span className="serial-number">#{serial}</span>
          <button 
            className={`favorite-btn ${device.favorite ? 'active' : ''}`} 
            onClick={handleFavoriteClick}
          >
            {device.favorite ? '⭐' : '☆'}
          </button>
        </div>
        <div className={`status-badge ${device.online ? 'online' : 'offline'}`}>
          {device.online ? '●' : '○'} 
          {device.online ? 'ON' : 'OFF'}
        </div>
      </div>
      
      <div className="device-name">
        {String(device.model || device.deviceName || 'Unknown')}
      </div>
      
      <div className="device-id">
        ID: {String(device.id || '')}
      </div>
      
      <div className="device-stats">
        <div className="battery-info">
          <span className={`battery-icon ${batteryColorClass}`}>🔋</span>
          <span className="battery-level">{battery}%</span>
        </div>
        
        <div className="install-time">
          <span>⏱️</span>
          <span>{formatInstallTime()}</span>
        </div>
        
        {device.online && <div className="online-indicator"></div>}
      </div>
      
      <div className="battery-bar">
        <div className={`battery-progress ${progressClass}`} style={{ width: `${battery}%` }}></div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if device properties that affect display have changed
  return (
    prevProps.device.id === nextProps.device.id &&
    prevProps.device.online === nextProps.device.online &&
    prevProps.device.favorite === nextProps.device.favorite &&
    prevProps.device.battery === nextProps.device.battery &&
    prevProps.device.model === nextProps.device.model &&
    prevProps.device.installTime === nextProps.device.installTime &&
    prevProps.device.installDate === nextProps.device.installDate &&
    prevProps.serial === nextProps.serial
  );
});

DeviceCard.displayName = 'DeviceCard';

export default DeviceCard;
